import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  DefaultCreditCardDelimiter,
  formatCreditCard,
  registerCursorTracker,
} from "cleave-zen";
import { useEffect, useRef } from "react";
import { ApiError, Order } from "@/api/SolarCloudApi";
import {
  creditCardSchema,
  OrderPayment,
  PaymentType,
  useCreatePayment,
} from "@/queries/payments/useCreatePayment";
import { toast } from "sonner";
import LoadingSection from "@/components/LoadingSection";
import { DateInput } from "@/components/ui/DateInput";
import { usePixelContext } from "@/contexts/MetaPixelContext";
import { TrackableEventNameEnum } from "react-meta-pixel";

type TCreditCardSchema = z.infer<typeof creditCardSchema>;

interface MintPaymentFormProps {
  order: Order;
  onSuccess: (order: Order) => void;
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  beforePayment?: () => Promise<boolean>;
}

export default function MintPaymentForm({
  order,
  onSuccess,
  setIsSubmitting,
  beforePayment,
}: MintPaymentFormProps) {
  const { pixel } = usePixelContext();

  const form = useForm<TCreditCardSchema>({
    resolver: zodResolver(creditCardSchema),
    defaultValues: {
      number: "",
      name: "",
      expiry: "",
      cvc: "",
    },
  });

  const { mutateAsync: makePayment } = useCreatePayment({
    mutationConfig: {
      onSuccess: (data) => {
        onSuccess(data);
      },
      onError: (error) => {
        if (error instanceof ApiError) {
          form.setError("number", {
            type: "manual",
            message: error.body.message,
          });
          toast.error(
            error.body.message || "Payment failed. Please try again."
          );
        } else {
          toast.error(error?.toString() || "Payment failed. Please try again.");
        }
      },
      onSettled: () => {
        setIsSubmitting(false);
      },
    },
  });
  const creditCardRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (data: TCreditCardSchema) => {
    if (beforePayment) {
      const canProceed = await beforePayment();
      if (!canProceed) {
        return;
      }
    }

    setIsSubmitting(true);
    if (pixel) {
      pixel.trackEvent(TrackableEventNameEnum.AddPaymentInfo);
    }
    const payment = {
      type: PaymentType.Mint,
      details: {
        number: data.number,
        name: data.name,
        expiry: data.expiry.replace("/", ""),
        cvc: data.cvc,
        valid: true,
      },
      valid: true,
    };

    const orderPayment = {
      order,
      payment,
    } as OrderPayment;

    await makePayment(orderPayment);
  };

  // useEffect(() => {
  //   if (!creditCardRef.current) return;
  //   return registerCursorTracker({
  //     input: creditCardRef.current,
  //     delimiter: DefaultCreditCardDelimiter,
  //   });
  // }, []);

  const expiryRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (creditCardRef.current) {
      const cleanupCC = registerCursorTracker({
        input: creditCardRef.current,
        delimiter: DefaultCreditCardDelimiter,
      });

      const cleanupExpiry = expiryRef.current
        ? registerCursorTracker({
          input: expiryRef.current,
          delimiter: "/",
        })
        : undefined;

      return () => {
        cleanupCC?.();
        cleanupExpiry?.();
      };
    }
  }, []);

  return (
    <div className="relative">
      {form.formState.isSubmitting && (
        <div className="absolute -inset-1 z-10 flex items-center justify-center bg-brand-neutral-800">
          <LoadingSection />
        </div>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="number"
              render={({ field }) => {
                const combinedRef = mergeRefs(field.ref, creditCardRef);

                return (
                  <FormItem>
                    <FormLabel>Card Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="XXXX XXXX XXXX XXXX"
                        {...field}
                        ref={combinedRef}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          const formatted = formatCreditCard(rawValue);
                          field.onChange(formatted);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cardholder Name</FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex w-full justify-between gap-6 items-start">
              <FormField
                control={form.control}
                name="expiry"
                render={({ field }) => {
                  const combinedExpiryRef = mergeRefs(field.ref, expiryRef);
                  return (
                    <FormItem className="w-40">
                      <FormLabel>Expiration Date</FormLabel>
                      <FormControl>
                        <DateInput
                          placeholder="MM/YY"
                          {...field}
                          ref={combinedExpiryRef}
                          onValueChange={(val) => field.onChange(val)}
                          datePattern={["m", "y"]}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="cvc"
                render={({ field }) => (
                  <FormItem className="w-20">
                    <FormLabel>CVC</FormLabel>
                    <FormControl>
                      <Input placeholder="XXX" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="w-full">
              <Button
                disabled={form.formState.isSubmitting || !form.formState.isValid}
                type="submit"
                className="w-full"
              >
                {form.formState.isSubmitting ? "Processing..." : order.billingOption === "recurring" ? "Pay First Payment" : "Pay Deposit"}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

function mergeRefs<T>(...refs: React.Ref<T>[]): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(value);
      } else if (typeof ref === "object" && ref !== null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}
